import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { useTranslation } from "react-i18next";
import { openTelegramLink } from "@telegram-apps/sdk-react";
import { Ban, TriangleAlert, Headset } from "lucide-react";

interface BlockedUserSheetProps {
  open: boolean;
  reason?: string | null;
}

const SUPPORT_URL = "https://t.me/DON_supportbot";

/**
 * Full-screen, non-dismissable block screen.
 *
 * Shown when the current user's account has been blocked
 * (profile.is_active === false). It cannot be closed, swiped away, or
 * dismissed, and every backend action is independently rejected with 403 — so
 * a blocked account is fully locked out of the app. The only way out is to
 * reach support (@DON_supportbot) if the user believes the block is a mistake.
 * Unblocking the account from the admin makes this disappear on the next
 * profile fetch, with no other changes. All copy is localized (uz / ru / en).
 */
export default function BlockedUserSheet({
  open,
  reason,
}: BlockedUserSheetProps) {
  const { t } = useTranslation();

  const handleContactSupport = () => {
    if (openTelegramLink.isAvailable()) {
      openTelegramLink(SUPPORT_URL);
    } else {
      window.open(SUPPORT_URL, "_blank");
    }
  };

  return (
    <Drawer
      open={open}
      // No onOpenChange handler + dismissible={false} => the sheet cannot be
      // closed, swiped down, or dismissed by tapping the overlay.
      dismissible={false}
      shouldScaleBackground={false}
    >
      <DrawerContent
        className="h-[100dvh] max-h-[100dvh] rounded-t-none border-none bg-dark"
        // Belt-and-braces with dismissible={false}: swallow the drag gesture so
        // the sheet can never start a swipe-to-close.
        onPointerDownCapture={(e) => e.stopPropagation()}
      >
        <div className="relative flex h-full flex-col overflow-hidden px-6 pb-safe-bottom text-center">
          {/* Main content — vertically centered */}
          <div className="flex flex-1 flex-col items-center justify-center gap-6">
          {/* Icon badge — perfectly concentric via nested flex circles */}
          <div className="relative flex items-center justify-center">
            <div className="pointer-events-none absolute h-52 w-52 rounded-full bg-red/20 blur-[90px]" />
            <div className="relative flex h-[128px] w-[128px] items-center justify-center rounded-full bg-red/[0.06]">
              <div className="flex h-[96px] w-[96px] items-center justify-center rounded-full bg-red-1">
                <div className="flex h-[76px] w-[76px] items-center justify-center rounded-full bg-red">
                  <Ban className="h-9 w-9 text-white" strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="relative max-w-[300px] text-[22px] font-bold uppercase leading-130 text-white">
            {t("account_blocked_title")}
          </h1>

          {/* Reason card — glassy translucent panel over the dark background */}
          <div className="relative w-full max-w-[340px] rounded-xl border border-white/08 bg-white/[0.04] p-4 text-center backdrop-blur-[10px]">
            <div className="flex items-center justify-center gap-2">
              <TriangleAlert className="size-5 shrink-0 text-red" strokeWidth={2.2} />
              <span className="text-sm font-semibold leading-130 text-white">
                {t("account_blocked_reason_label")}
              </span>
            </div>
            <hr className="my-3 h-px w-full border-none bg-white/08" />
            <p className="whitespace-pre-line text-sm font-normal leading-130 text-white/80">
              {reason?.trim() ? reason : t("account_blocked_default")}
            </p>
          </div>
          </div>

          {/* Support (pinned to the bottom) — for users who believe the block is a mistake */}
          <div className="relative mx-auto flex w-full max-w-[340px] flex-col items-center gap-2.5 pt-6">
            <p className="text-xs font-normal leading-130 text-gray-1">
              {t("account_blocked_support_hint")}
            </p>
            <button
              type="button"
              onClick={handleContactSupport}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-yellow-linear text-sm font-semibold text-[#101017] transition-opacity active:opacity-90"
            >
              <Headset className="h-[18px] w-[18px]" strokeWidth={2.2} />
              {t("account_blocked_support_cta")}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
